import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad';
import { getArticleImage } from '~scenes/LongArticleWidget/utils/utils';
import SportsSelectionViewContainer from '~shared/components/react/SportsSelectionView/SportsSelectionViewContainer';
import { getGaAttributeValueSpotlight } from '~shared/utils/google-analytics';

import { Link } from '~spa/Components/Link/Link';
import WidgetContainer from '~shared/ui/WidgetContainer';
import { seoUrlEvent } from '~spa/Utils/SeoUrls.js';
import useSpotlight from '../useSpotlight';

import './styles.scss';

const BASE_CLASS = 'full-spotlight-widget';
const ARTICLE_TYPE = 'spotlight';
const GTM_ONE_FOR_YOU_WIDGET = 'one-for-you-widget';

const SpotlightWidgetComponent = ({
    betLocate,
    data,
    sportSlug: sportPageSlug,
}) => {
    const [ t ] = useTranslation();
    const { lazyLoadHasLoaded, lazyLoadIsEmpty } = useLazyLoadContext();
    const [spotlight, setSpotlight] = useState({});

    useEffect(() => {
        if (data?.article_data && data?.selection_pointers) {
            const {
                article_data: articleData,
                selection_pointers: selectionPointers,
            } = data;
            setSpotlight({
                articleData,
                selectionPointers,
            });
            lazyLoadHasLoaded();
        } else if (data.error) {
            lazyLoadIsEmpty();
        }
    }, [data]);

    const {
        articleData,
        selectionPointers = {},
    } = spotlight;

    const {
        event_id: eventId,
        market_id: marketId,
        selection_id: selectionId,
    } = selectionPointers;

    const event = articleData?.events?.[eventId];
    const selection = event?.markets?.[marketId]?.selections?.[selectionId];

    if (!selection) {
        return null;
    }

    const {
        title,
        text,
        image_filename: imageFileName,
    } = articleData;

    const {
        sport_slug: sportSlug,
        sport_slug_i18n: sportSlugI18n,
        category_slug: categorySlug,
        category_slug_i18n: categorySlugI18n,
        tournament_slug: tournamentSlug,
        tournament_slug_i18n: tournamentSlugI18n,
        slug: eventSlug,
        event_slug_i18n: eventSlugI18n,
    } = event;

    const eventUrl = seoUrlEvent([
        sportSlugI18n || sportSlug,
        categorySlugI18n || categorySlug,
        tournamentSlugI18n || tournamentSlug,
        eventSlugI18n || eventSlug,
    ].join('/'), eventId);

    const gtmAttribute = `${sportPageSlug || 'homepage'};${GTM_ONE_FOR_YOU_WIDGET}`;
    const gtmVisitEvent = `${gtmAttribute};visit-event`;

    const selectionViewProps = {
        selection,
        gtmAttribute,
        commonDataEvents: {
            [eventId]: {
                ...event,
                event_url: eventUrl,
            },
        },
    };

    const imageUrl = imageFileName && getArticleImage(imageFileName, ARTICLE_TYPE);
    const bgImageStyle = {
        backgroundImage: `url("${imageUrl}")`,
    };

    return (
        <>
            <div className={`${BASE_CLASS}`}>
                <WidgetContainer
                    title={t('one_for_you')}
                    titleIconClassName='one-for-you'
                    titleLink={eventUrl}
                    gtmAttribute={gtmVisitEvent}
                    gaComponent={getGaAttributeValueSpotlight(betLocate)}
                >
                    <div className={`${BASE_CLASS}__container`}>
                        <Link
                            to={eventUrl}
                            className={`${BASE_CLASS}__link`}
                            gtmAttribute={gtmVisitEvent}
                        ></Link>
                        <div className={`${BASE_CLASS}__figure`}>
                                <div className={`${BASE_CLASS}__figure__bg` } style={bgImageStyle}></div>
                                <div className={`${BASE_CLASS}__figure__img`} style={bgImageStyle}></div>
                                <div className={`${BASE_CLASS}__figure__mask`}></div>
                        </div>
                        <div className={`${BASE_CLASS}__content`}>
                            <strong className={`${BASE_CLASS}__content-title`}>{title}</strong>
                            <p className={`${BASE_CLASS}__content-text`}>{text}</p>
                        </div>
                        <div className={`${BASE_CLASS}__selection`}>
                            <SportsSelectionViewContainer {...selectionViewProps} />
                        </div>
                    </div>
                </WidgetContainer>
            </div>
        </>
    );
};

SpotlightWidgetComponent.propTypes = {
    betLocate: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    sportSlug: PropTypes.string,
};

SpotlightWidgetComponent.defaultProps = {
    sportSlug: '',
};

const SpotlightWidgetNoData = (props) => {
    const { spotlightData } = useSpotlight({ sportSlug: props?.sportSlug });

    return <SpotlightWidgetComponent {...props} data={spotlightData} />;
};

SpotlightWidgetNoData.propTypes = {
    sportSlug: PropTypes.string,
};

const SpotlightWidgetEntry = (props) => {
    const { data } = props;

    if (data) {
        return <SpotlightWidgetComponent {...props} />;
    }

    return <SpotlightWidgetNoData {...props} />;
};

SpotlightWidgetEntry.propTypes = {
    data: PropTypes.object,
};

export default SpotlightWidgetEntry;
